import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Avatar, ProBadge, Stars, timeAgo } from "@/components/admin-control/Parts";
import { money } from "@/components/admin-control/BalanceChart";
import { Ban, FlaskConical, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const Empty = ({ children }) => <p className="text-sm text-zinc-500 py-6 text-center">{children}</p>;
const Row = ({ title, sub, right, id }) => (
  <div data-testid={id} className="flex items-start gap-3 py-2.5 border-b border-white/5 last:border-0">
    <div className="flex-1 min-w-0"><p className="text-sm text-white truncate">{title}</p><p className="text-xs text-zinc-500 truncate">{sub}</p></div>
    <div className="text-right text-xs text-zinc-400 shrink-0">{right}</div>
  </div>
);

function Actions({ u, refresh }) {
  const ban = async () => {
    try { await api.post(`/admin/${u.banned ? "unban" : "ban"}/${u.user_id}`); toast.success(u.banned ? "User unbanned" : "User banned and signed out everywhere"); refresh(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const tester = async (on) => {
    try { await api.post("/admin/testers", { email: u.email, is_tester: on }); toast.success(on ? "Marked as tester" : "Tester removed"); refresh(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="grid grid-cols-2 gap-2 mt-4">
      <label className="flex items-center justify-between gap-2 rounded-xl bg-[#121214] border border-white/5 px-3 h-11 text-sm">
        <span className="flex items-center gap-1.5"><FlaskConical className="h-4 w-4 text-amber-300" /> Tester</span>
        <Switch data-testid="detail-tester-toggle" checked={u.is_tester} onCheckedChange={tester} />
      </label>
      {u.banned ? (
        <button data-testid="detail-unban-btn" onClick={ban} className="h-11 rounded-xl border border-white/15 text-sm font-semibold flex items-center justify-center gap-1.5 hover:bg-white/5"><RotateCcw className="h-4 w-4" /> Unban</button>
      ) : (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button data-testid="detail-ban-btn" className="h-11 rounded-xl bg-red-600 hover:bg-red-500 text-white text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors"><Ban className="h-4 w-4" /> Ban user</button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-[#1A1A1E] border-white/10 text-white">
            <AlertDialogHeader>
              <AlertDialogTitle>Ban {u.name}?</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">They'll be signed out on every device immediately and can't log back in until you unban them.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="ban-cancel" className="bg-transparent border-white/15 text-white hover:bg-white/5">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="ban-confirm" onClick={ban} className="bg-red-600 hover:bg-red-500">Ban and sign out</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

export function UserDetailSheet({ userId, onClose, currency }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-user", userId], enabled: !!userId,
    queryFn: async () => (await api.get(`/admin/control/users/${userId}`)).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-user", userId] }); qc.invalidateQueries({ queryKey: ["admin-control-users"] }); qc.invalidateQueries({ queryKey: ["admin-control-summary"] }); };
  const u = data?.user;
  return (
    <Sheet open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" data-testid="user-detail-sheet" className="w-full sm:max-w-md bg-[#1A1A1E] border-white/10 text-white overflow-y-auto">
        {!u ? <div className="h-40 mt-10 rounded-xl bg-white/5 animate-pulse" /> : (
          <>
            <SheetHeader className="text-left">
              <div className="flex items-center gap-3">
                <Avatar u={u} size="h-14 w-14 text-lg" />
                <div className="min-w-0">
                  <SheetTitle className="text-white flex items-center gap-2">{u.name} {u.is_pro && <ProBadge id="detail-pro" />}</SheetTitle>
                  <SheetDescription className="text-zinc-400 truncate">{u.email}</SheetDescription>
                </div>
              </div>
            </SheetHeader>
            <div className="grid grid-cols-4 gap-2 mt-4 text-center">
              {[["Paid", u.contribution > 0 ? money(u.contribution, currency) : "—"], ["Calls", data.calls.length], ["Matches", data.matches.length], ["Reports", data.reports.filter((r) => r.direction === "against").length]].map(([l, v]) => (
                <div key={l} className="rounded-xl bg-[#121214] border border-white/5 py-2"><p className="text-[10px] uppercase text-zinc-500">{l}</p><p data-testid={`detail-stat-${l.toLowerCase()}`} className="font-bold text-sm mt-0.5">{v}</p></div>
              ))}
            </div>
            <p className="text-xs text-zinc-500 mt-3">Joined {u.created_at ? new Date(u.created_at).toLocaleDateString("en-GB") : "—"} · {u.city || "No city"}{u.country ? `, ${u.country}` : ""}{u.verified ? " · Photo verified" : ""}{u.banned ? " · BANNED" : ""}</p>
            <Actions u={u} refresh={refresh} />
            <Tabs defaultValue="calls" className="mt-5">
              <TabsList className="bg-[#121214] w-full">
                <TabsTrigger value="calls" data-testid="detail-tab-calls" className="flex-1">Calls</TabsTrigger>
                <TabsTrigger value="matches" data-testid="detail-tab-matches" className="flex-1">Matches</TabsTrigger>
                <TabsTrigger value="reports" data-testid="detail-tab-reports" className="flex-1">Reports</TabsTrigger>
              </TabsList>
              <TabsContent value="calls">
                {data.calls.length === 0 ? <Empty>No calls yet.</Empty> : data.calls.map((c, i) => (
                  <Row key={i} id={`detail-call-${i}`} title={`${c.kind} with ${c.partner}`} sub={new Date(c.started_at).toLocaleString("en-GB")} right={timeAgo(c.started_at)} />
                ))}
                {data.ratings.length > 0 && <p className="text-xs text-zinc-500 mt-3 mb-1">Their call ratings</p>}
                {data.ratings.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 py-1"><Stars n={r.stars} id={`detail-rating-${i}`} /><span className="text-xs text-zinc-400 truncate">{r.comment || r.pair}</span></div>
                ))}
              </TabsContent>
              <TabsContent value="matches">
                {data.matches.length === 0 ? <Empty>No matches yet.</Empty> : data.matches.map((m) => (
                  <Row key={m.id} id={`detail-match-${m.id}`} title={m.partner} sub={m.event || "Event"} right={<span className="capitalize">{m.status.replace(/_/g, " ")}</span>} />
                ))}
              </TabsContent>
              <TabsContent value="reports">
                {data.reports.length === 0 ? <Empty>No reports.</Empty> : data.reports.map((r) => (
                  <Row key={r.id} id={`detail-report-${r.id}`} title={`${r.direction === "against" ? "Reported by" : "Reported"} ${r.other}`} sub={`${r.reason}${r.details ? ` — ${r.details}` : ""}`} right={<span className={r.status === "open" ? "text-amber-300" : ""}>{r.status}</span>} />
                ))}
              </TabsContent>
            </Tabs>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
